import { motion, useInView, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { PADDLES } from '../../data/technicals';

const decimalsOf = (v) => (String(v).split('.')[1] || '').length;

// Animated number that counts up once `active` (or when it scrolls into view if no `active` given)
export const CountUp = ({ value, active, className = '' }) => {
  const ref = useRef(null);
  const ownInView = useInView(ref, { once: true, margin: '0px 0px -5% 0px' });
  const go = active ?? ownInView;
  const mv = useMotionValue(0);
  const spring = useSpring(mv, { stiffness: 110, damping: 26, restDelta: 0.0005 });
  const d = decimalsOf(value);
  const text = useTransform(spring, (v) => v.toFixed(d));
  useEffect(() => { if (go) mv.set(value); }, [go, value, mv]);
  return <motion.span ref={ref} className={className}>{text}</motion.span>;
};

// Two-bar comparison for one metric. Both models get the same visual weight.
export const MetricBars = ({ metric, dark = true, compact = false }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '0px 0px -5% 0px' });
  const rows = [PADDLES.hexcore, PADDLES.profoam];
  const text = dark ? 'text-white' : 'text-[#0A0A0A]';
  const sub = dark ? 'text-zinc-400' : 'text-neutral-500';
  const track = dark ? 'bg-white/[0.06]' : 'bg-neutral-200/70';

  return (
    <div ref={ref} className="w-full" data-testid={`metric-bars-${metric.key}`}>
      {!compact && (
        <div className="flex items-baseline justify-between gap-4 mb-4">
          <div>
            <p className={`font-mono text-[10px] uppercase tracking-[0.3em] ${sub}`}>{metric.label}</p>
            {metric.condition && <p className={`mt-1 text-xs ${sub}`}>{metric.condition}</p>}
          </div>
          {metric.leadNote && (
            <span className={`shrink-0 font-mono text-[10px] uppercase tracking-[0.2em] ${sub}`}>{metric.leadNote}</span>
          )}
        </div>
      )}
      <div className={compact ? 'space-y-2' : 'space-y-3.5'}>
        {rows.map((p) => {
          const v = metric[p.slug];
          const pct = Math.max(4, (v / metric.max) * 100);
          const note = p.slug === 'hexcore' ? metric.hexNote : metric.proNote;
          return (
            <div key={p.slug} className={`grid items-center gap-3 ${compact ? 'grid-cols-[64px_1fr_auto]' : 'grid-cols-[72px_1fr_auto] sm:grid-cols-[92px_1fr_auto]'}`}>
              <span className={`font-heading font-bold text-xs sm:text-sm uppercase tracking-wider ${text}`} style={{ color: p.accent }}>
                {p.name}
              </span>
              <div className={`relative h-2.5 sm:h-3 rounded-full overflow-hidden ${track}`}>
                <motion.div
                  className="absolute inset-y-0 left-0 rounded-full"
                  style={{ background: `linear-gradient(90deg, ${p.accent}CC, ${p.accent})`, boxShadow: `0 0 18px ${p.accent}55` }}
                  initial={{ width: 0 }}
                  animate={inView ? { width: `${pct}%` } : { width: 0 }}
                  transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: p.slug === 'hexcore' ? 0.05 : 0.2 }}
                />
              </div>
              <div className={`text-right ${compact ? 'min-w-[72px]' : 'min-w-[88px] sm:min-w-[120px]'}`}>
                <span className={`font-mono text-sm sm:text-base ${text}`}>
                  <CountUp value={v} active={inView} /> <span className={`text-[10px] sm:text-xs ${sub}`}>{metric.unit}</span>
                </span>
                {!compact && note && <p className={`text-[10px] sm:text-[11px] leading-tight ${sub}`}>{note}</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
